import { Link, Text } from "@shopify/polaris"
import React, { Component } from "react"
import { WithTranslation, withTranslation } from "react-i18next"

import { ITrackingResultItem, TrackInfoItem as ITrackInfoItem } from "@/api/TP2/tracking-info.d"
import { ITrackingPageStatus } from "@/models/IUserInfo"
import stores from "@/stores"

import styles from "./TrackInfo.module.scss"

interface IProps extends WithTranslation<"common"> {
  tracking: ITrackingResultItem | undefined;
}

interface IStates {
  expand: boolean
}

class TrackInfo extends Component<IProps, IStates> {
  state: IStates = {
    expand: true, // 默认打开
  }

  render() {
    const { t, tracking } = this.props,
      trackInfoLength = tracking?.trackinfo.length ?? 0,
      renderShortItem = [0, 1, trackInfoLength - 1],
      checkPointStatus = this.getCheckPointStatus("blank")

    return (<div className={styles.TrackInfo}>
      {tracking?.trackinfo.map((item, index) => {
        const key = `track-info-item-${index}`

        // 如果不需要「显示更多」就直接渲染
        if (this.state.expand && trackInfoLength > 3) {
          return this.infoMarkup(item, key)
        }

        // 仅仅显示「3 条记录」
        if (renderShortItem.includes(index)) {
          return this.infoMarkup(item, key)
        }

        // 展示「显示更多信息按钮」
        if (index === trackInfoLength - 2) {
          return (
            <div className={styles.TrackInfoItem} key={key}>
              <div className={styles.TrackInfoItem__Icon}>
                <img src={checkPointStatus?.img} alt={checkPointStatus?.en} data-status="blank" />
              </div>

              <div className={styles.TrackInfoItem__Info}>
                <Link removeUnderline onClick={() => {
                  this.setState({ expand: true })
                }}
                >{t("ShowMoreDetails")}</Link>
              </div>
            </div>
          )
        }

        return undefined
      })}
    </div>)
  }

  public getTrackText(item: ITrackInfoItem) {
    const local = item.Details
    let event = item.StatusDescription

    if (local && event !== local) event += `, ${local}`

    return event.trim()
  }

  public infoMarkup(trackInfo: ITrackInfoItem, key: string) {
    const checkPointStatus = this.getCheckPointStatus(trackInfo.checkpoint_status as keyof ITrackingPageStatus)
    const info = this.getTrackText(trackInfo)

    if (!info) return null
    
    return (
      <div className={styles.TrackInfoItem} key={key}>
        <div className={styles.TrackInfoItem__Icon}>
          <img src={checkPointStatus?.img} alt={checkPointStatus?.en} data-status={trackInfo.checkpoint_status} />
        </div>

        <div className={styles.TrackInfoItem__Info}>
          <Text variant={"bodyMd"} as={"p"} tone={"subdued"}>{trackInfo.Date}</Text>
          <Text variant={"bodyMd"} as={"p"}>
            <span dangerouslySetInnerHTML={{ __html: this.sanitizeHtml(info) }} />
          </Text>
        </div>
      </div>
    )
  }

  private getCheckPointStatus = (status: keyof ITrackingPageStatus) => stores.userInfoSore.data.tracking_page?.status_config[status]

  private sanitizeHtml(html: string): string {
    try {
      // SSR 兜底：移除所有标签，只保留纯文本
      if (typeof window === "undefined") return html.replace(/<[^>]*>/g, "")

      const container = document.createElement("div")

      container.innerHTML = html

      const ALLOWED_TAGS = new Set([
        "A", "B", "STRONG", "I", "EM", "BR", "SPAN", "P",
      ])

      const ALLOWED_ATTRS: Record<string, Set<string>> = {
        A: new Set(["href", "rel", "target"]),
        B: new Set(),
        STRONG: new Set(),
        I: new Set(),
        EM: new Set(),
        BR: new Set(),
        SPAN: new Set(),
        P: new Set(),
      }

      const isHttpLike = (value: string) => /^https?:\/\//i.test(value)

      const enforceHttpForHref = (value: string) => value.replace(/^https:\/\//i, "http://")

      const unwrapElement = (el: Element) => {
        const parent = el.parentNode

        if (!parent) {
          el.remove()
          return
        }
        while (el.firstChild) parent.insertBefore(el.firstChild, el)
        parent.removeChild(el)
      }

      const sanitizeElement = (el: Element) => {
        const tag = el.tagName

        // 移除明显危险的节点（即使在白名单内也不应该出现）
        const forbidden = ["SCRIPT", "IFRAME", "OBJECT", "EMBED", "FORM", "INPUT", "BUTTON", "STYLE", "LINK", "META"]

        if (forbidden.includes(tag)) {
          el.remove()
          return
        }

        // 非白名单标签：展开（保留其子节点文本）
        if (!ALLOWED_TAGS.has(tag)) {
          unwrapElement(el)
          return
        }

        // 清理属性
        Array.from(el.attributes).forEach((attr) => {
          const { name } = attr
          const value = (attr.value || "").trim()

          // 移除所有事件属性和 dataset、style
          if (/^on/i.test(name) || name === "style" || name.startsWith("data-")) {
            el.removeAttribute(name)
            return
          }

          // 非允许属性全部移除
          const allowed = ALLOWED_ATTRS[tag] ?? new Set<string>()

          if (!allowed.has(name)) {
            el.removeAttribute(name)
            return
          }

          // a 标签特殊处理
          if (tag === "A" && name === "href") {
            if (!isHttpLike(value)) {
              el.setAttribute("href", "#")
            } else {
              el.setAttribute("href", enforceHttpForHref(value))
            }
          }
        })

        // a：强制安全属性
        if (tag === "A") {
          el.setAttribute("rel", "noreferrer noopener")
          el.setAttribute("target", "_blank")

          // MARK: 后续 Shopify 样式升级，必须调整这里
          el.setAttribute("class", "Polaris-Link Polaris-Link--removeUnderline")
        }
      }

      const deepSanitize = (root: Element) => {
        // 先处理子节点，后处理自己，避免展开时丢失子节点处理
        Array.from(root.children).forEach((child) => deepSanitize(child))
        // 跳过容器本身
        if (root !== container) sanitizeElement(root)
      }

      Array.from(container.children).forEach((child) => deepSanitize(child))

      return container.innerHTML
    } catch (error) {
      console.error(error)

      return html
    }
  }
}

export default withTranslation("common")(TrackInfo)
